import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Activity, Terminal, Flame, Gauge, DollarSign, RefreshCw, Server, Brain } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import { StrategyPerformance } from './StrategyPerformance';
import { TradingViewChart } from './TradingViewChart';
import { MarketHeatmap } from './MarketHeatmap';
import SystemLogs from './SystemLogs'; 
import { ErrorBoundary } from '../components/ErrorBoundary';
import api from '../api/api'; 
import axios from 'axios';
import { AiAdvisor, AiInsight } from './AiAdvisor';

export interface LogEntry {
  id: number;
  time: string;
  message: string;
  type: 'INFO' | 'WARN' | 'ERROR';
}

interface DashboardProps {
  selectedSymbol: string;
  stats: any;
  balance: any;
  isBotRunning: boolean;
  toggleBot: () => void;
  btcStatus: any;
  chartData: any[];
  userPlan: string;
  strategyMode: 'AUTO' | 'TREND' | 'SCALP';
  setStrategyMode: (mode: 'AUTO' | 'TREND' | 'SCALP') => void;
  logs: LogEntry[];
}

export const Dashboard: React.FC<DashboardProps> = ({ 
  selectedSymbol, 
  stats: propStats, 
  balance, 
  isBotRunning, 
  toggleBot,
  btcStatus,
  chartData,
  userPlan,
  strategyMode,
  setStrategyMode,
  logs,
}) => {
  const { t } = useTranslation();
  
  // ✅ FIX: Добавлено состояние для пользователя
  const [userData, setUserData] = useState<any>(null);
  
  const [stats, setStats] = useState(propStats || { dailyProfit: 0, activeDeals: 0, winRate: 0 });
  const [fearIndex, setFearIndex] = useState({ value: 50, classification: 'Neutral' });
  const [aiInsight, setAiInsight] = useState<AiInsight | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const defaultInsight: AiInsight = {
    type: 'INFO',
    titleKey: 'platform.ai.scanning', 
    messageKey: 'AI is analyzing real-time market data...',
    metric: 'Monitoring'
  };

  useEffect(() => {
    if (propStats) setStats((prev: any) => ({ ...prev, ...propStats }));
  }, [propStats]);

  // ✅ FIX: Загрузка данных пользователя для получения биржи (exchange)
  useEffect(() => {
    const fetchUser = async () => {
        try {
            const { data } = await api.get('/users/me');
            setUserData(data);
        } catch (e) {
            console.error("Error fetching user:", e);
        }
    };
    fetchUser();
  }, []);

  const fetchStats = useCallback(async () => {
      try {
          const res = await api.get('/trading/stats');
          setStats((prev: any) => ({ ...prev, ...res.data }));
      } catch (e) { console.error(e); }
  }, []);

  const fetchAiData = useCallback(async () => {
      try {
          const res = await api.get('/ai/insight');
          if (res.data) setAiInsight(res.data);
      } catch (e) { console.error(e); }
  }, []);

  const fetchAuxData = useCallback(async () => {
    try {
      const [fearRes] = await Promise.all([
          axios.get('https://api.alternative.me/fng/')
      ]);
      if (fearRes.data?.data?.[0]) {
          setFearIndex({ 
              value: parseInt(fearRes.data.data[0].value), 
              classification: fearRes.data.data[0].value_classification 
          });
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchAiData();
    fetchAuxData();
    const interval = setInterval(fetchAuxData, 60000); 
    return () => clearInterval(interval);
  }, [fetchStats, fetchAiData, fetchAuxData]);

  const handleManualRefresh = async () => {
      if (isRefreshing) return;
      setIsRefreshing(true);
      await Promise.all([
          fetchStats(),
          fetchAiData(),
          fetchAuxData(),
          new Promise(r => setTimeout(r, 800)) 
      ]);
      setIsRefreshing(false);
  };

  const cycleTradingMode = () => {
      const modes: ('AUTO' | 'TREND' | 'SCALP')[] = ['AUTO', 'TREND', 'SCALP'];
      const currentIndex = modes.indexOf(strategyMode);
      const nextMode = modes[(currentIndex + 1) % modes.length];
      setStrategyMode(nextMode);
  };

  const getModeColor = () => {
      if (strategyMode === 'SCALP') return 'text-orange-400';
      if (strategyMode === 'TREND') return 'text-blue-400';
      return 'text-purple-400';
  };

  const chartSymbol = selectedSymbol.replace('/', '');

  return (
    <div className="animate-in fade-in duration-700 pb-20 max-w-full space-y-6">
      
      {/* ================= HEADER ================= */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight flex items-center gap-2">
             <Terminal className="text-blue-500"/> {t('platform.dashboard.title')}
          </h2>
          <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-1 rounded-lg border border-slate-700">
                  <span className={`font-bold uppercase ${getModeColor()}`}>{strategyMode} MODE</span>
                  <button onClick={cycleTradingMode} className="p-1 hover:bg-slate-700 rounded-md transition-colors text-slate-400 hover:text-white">
                      <RefreshCw size={12} />
                  </button>
              </div>
              <div className="flex items-center gap-1.5 bg-emerald-500/10 px-3 py-1 rounded-lg border border-emerald-500/20">
                  <Server size={12} className="text-emerald-400" />
                  <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">{t('platform.dashboard.live')}</span>
              </div>
              <button onClick={handleManualRefresh} className="flex items-center gap-2 px-3 py-1 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-600/30 rounded-lg text-blue-400 text-[10px] font-bold uppercase tracking-wider transition-all">
                  <RefreshCw size={12} className={isRefreshing ? 'animate-spin' : ''} />
                  {isRefreshing ? t('platform.dashboard.updating') : t('platform.dashboard.refresh')}
              </button>
          </div>
        </div>

        <div className="flex items-center gap-6">
            <div className={`text-right p-4 rounded-2xl border shadow-xl min-w-[200px] relative overflow-hidden transition-all duration-300 ${balance.isPaper ? 'bg-indigo-900/40 border-indigo-500/30' : 'bg-[#1e293b] border-slate-800'}`}>
                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-tighter flex justify-end items-center gap-1">
                    <DollarSign size={10}/> 
                    {balance.isPaper ? t('platform.dashboard.virtualBal') : t('platform.dashboard.realBal')}
                </p>
                <p className={`text-2xl font-mono font-bold transition-all ${balance.isPaper ? 'text-indigo-300' : 'text-green-400'}`}>
                    ${Number(balance.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
            </div>
        </div>
      </header>

      {/* ================= PRO / PAPER BANNER ================= */}
      {balance.isPaper && (
        <div className={`p-4 rounded-2xl border flex items-center justify-between animate-in slide-in-from-top-4 duration-700 ${userPlan === 'FREE' ? 'bg-gradient-to-r from-indigo-900/40 to-blue-900/40 border-indigo-500/30' : 'bg-gradient-to-r from-emerald-900/20 to-teal-900/20 border-emerald-500/20'}`}>
           <div className="flex items-center gap-4">
              <div className={`p-2 rounded-xl ${userPlan === 'FREE' ? 'bg-indigo-500/20' : 'bg-emerald-500/20'}`}>
                 <Terminal size={24} className={userPlan === 'FREE' ? 'text-indigo-400' : 'text-emerald-400'} />
              </div>
              <div>
                 <h4 className="text-white font-bold text-sm uppercase tracking-wider">
                    {userPlan === 'FREE' ? t('platform.dashboard.paperTitleFree') : t('platform.dashboard.paperTitlePro')}
                 </h4>
                 <p className="text-slate-400 text-xs mt-0.5">
                    {userPlan === 'FREE' ? t('platform.dashboard.paperDescFree') : t('platform.dashboard.paperDescPro')}
                 </p>
              </div>
           </div>
           {userPlan === 'FREE' && (
               <button onClick={() => window.location.href = '/billing'} className="hidden sm:flex px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition-colors items-center gap-2">
                  {t('platform.dashboard.connectReal')}
               </button>
           )}
        </div>
      )}

      {/* ================= AI ADVISOR (INSIGHT) - TOP POSITION ================= */}
      <div className="w-full">
         <AiAdvisor insight={aiInsight || defaultInsight} />
      </div>

      {/* ================= STATUS BAR ================= */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4 lg:gap-8">
          <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full shadow-lg transition-colors duration-500 ${isBotRunning ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
              <span className={`text-sm font-bold tracking-wide ${isBotRunning ? 'text-slate-200' : 'text-slate-500'}`}>
                {isBotRunning ? t('platform.actions.active') : t('platform.actions.paused')}
              </span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${btcStatus.isSafe ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
              <span>BTC: {btcStatus.btcChange}%</span> {btcStatus.isSafe ? '✅' : '🛑'}
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-400 text-[11px] font-bold border border-purple-500/20">
              <Gauge size={14} /> <span>Fear: {fearIndex.value}</span> 
          </div>
        </div>
        <button onClick={toggleBot} className={`px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all active:scale-95 flex items-center gap-2 border ${isBotRunning ? 'bg-red-500/10 text-red-500 border-red-500/50 hover:bg-red-500 hover:text-white' : 'bg-green-600 text-white border-green-500'}`}>
             {isBotRunning ? <>{t('platform.actions.stop')}</> : <>{t('platform.actions.start')}</>}
        </button>
      </div>

      {/* ================= QUICK STATS ================= */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 rounded-3xl border border-green-500/20 bg-green-900/10 shadow-xl transition-all duration-500 hover:border-green-500/40">
          <p className="text-slate-400 text-xs font-bold uppercase mb-1">{t('platform.dashboard.pnl')}</p>
          <h3 className={`text-3xl font-bold ${Number(stats.dailyProfit) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {Number(stats.dailyProfit) > 0 ? '+' : ''}{stats.dailyProfit} $
          </h3>
        </div>
        <div className="bg-[#1e293b] border border-slate-800 p-6 rounded-3xl shadow-xl hover:border-slate-700 transition-all">
          <p className="text-slate-400 text-xs font-bold uppercase mb-1">{t('platform.dashboard.deals')}</p>
          <h3 className="text-3xl font-bold text-white">
              {stats.activeDeals ?? stats.activeOrders ?? 0} <span className="text-sm font-normal text-slate-500 ml-1">pos</span>
          </h3>
        </div>
        <div className="bg-[#1e293b] border border-slate-800 p-6 rounded-3xl shadow-xl hover:border-slate-700 transition-all">
          <p className="text-slate-400 text-xs font-bold uppercase mb-1">{t('platform.dashboard.winrate')}</p>
          <h3 className={`text-3xl font-bold ${Number(stats.winRate) >= 50 ? 'text-blue-400' : 'text-orange-400'}`}>
             {Number(stats.winRate || 0).toFixed(1)}%
          </h3>
        </div>
      </div>

      {/* ================= MAIN TRADINGVIEW ================= */}
      <div className="w-full">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2 uppercase text-xs tracking-widest px-2">
            <TrendingUp size={16} className="text-blue-500" /> {t('platform.dashboard.chart')}: {chartSymbol}
        </h3>
        <div className="h-[500px] rounded-[32px] overflow-hidden border border-slate-800 shadow-2xl bg-[#131722]">
           <ErrorBoundary>
              <TradingViewChart 
                symbol={selectedSymbol} 
                exchange={userData?.exchange || 'BINANCE'} // ✅ Теперь userData определен и ошибок не будет
            />
           </ErrorBoundary>
        </div>
      </div>

      {/* ================= ROW: PNL + HEATMAP (50/50) ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* PnL dynamics */}
        <div className="bg-[#1e293b] border border-slate-800 p-6 rounded-[24px] shadow-lg flex flex-col h-[400px]">
          <h3 className="text-white font-bold mb-4 text-xs uppercase flex items-center gap-2 tracking-widest">
            <Activity size={14} className="text-green-500" /> {t('platform.dashboard.pnlDyn')}
          </h3>
          <div className="flex-1 w-full min-h-0">
            {chartData && chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="date" hide /> <YAxis stroke="#64748b" fontSize={10} />
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px' }} itemStyle={{ color: '#fff' }} />
                  <Area type="monotone" dataKey="profit" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorProfit)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-600 gap-2"><Activity size={32} className="opacity-20" /><span className="text-sm italic">No data yet</span></div>
            )}
          </div>
        </div>

        {/* Heatmap */}
        <div className="bg-[#1e293b] border border-slate-800 p-6 rounded-[24px] shadow-lg flex flex-col h-[400px]">
          <h3 className="text-white font-bold mb-4 text-xs uppercase flex items-center gap-2 tracking-widest">
            <Flame size={14} className="text-orange-500" /> {t('platform.dashboard.heatmap')}
          </h3>
          <div className="flex-1 w-full min-h-0 rounded-xl overflow-hidden border border-slate-700/50">
            <MarketHeatmap />
          </div>
        </div>
      </div>

      {/* ================= AI STRATEGY ANALYSIS (WIDE) ================= */}
      <div className="bg-[#1e293b] border border-slate-800 p-6 rounded-[24px] shadow-xl">
        <h3 className="text-white font-bold mb-6 text-xs uppercase flex items-center gap-2 tracking-widest">
          <Brain size={16} className="text-purple-500" /> AI Strategy Analysis
        </h3>
        <div className="w-full">
            <StrategyPerformance />
        </div>
      </div>

      {/* ================= SYSTEM LOGS ================= */}
      <div className="bg-[#0f172a] border border-slate-800 rounded-[24px] p-2 shadow-xl">
         <SystemLogs logs={logs} />
      </div>
    </div>
  );
};